import { type ReactNode, useCallback, useRef } from "react";
import SignatureCanvas from "react-signature-canvas";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";

import { dataUrlToFile } from "utils/common";

const Root = styled(Box)(({ theme }) => ({
  "&.MuiBox-root": {
    borderRadius: "4px",
    border: `1px solid ${theme.palette.common.black}`
  }
}));

type SignatureDeskProps = {
  width: number;
  height: number;
  beforeButtonSlot?: ReactNode;
  isSubmitButton?: boolean;
  submitButtonText?: string;
  isLoading?: boolean;
  onCapture: (file: File) => void;
};

export default function SignatureDesk({
  width,
  height,
  isSubmitButton,
  isLoading,
  submitButtonText = "Confirm Signature",
  beforeButtonSlot,
  onCapture
}: SignatureDeskProps) {
  const ref = useRef<SignatureCanvas>(null);
  const handleCapture = useCallback(async () => {
    if (ref.current) {
      const canvas = ref.current.getTrimmedCanvas();
      const base64 = canvas.toDataURL("image/png");
      const file = await dataUrlToFile(base64, "signature.png");
      onCapture(file);
    }
  }, [onCapture]);

  return (
    <>
      <Root>
        <SignatureCanvas
          ref={ref}
          penColor="black"
          backgroundColor="rgba(0,0,0,0)"
          canvasProps={{
            width,
            height
          }}
        />
      </Root>
      <Stack rowGap={2} sx={{ mt: 2 }}>
        {beforeButtonSlot}
        <LoadingButton
          type={isSubmitButton ? "submit" : undefined}
          variant="contained"
          loading={isLoading}
          onClick={handleCapture}
        >
          {submitButtonText}
        </LoadingButton>
      </Stack>
    </>
  );
}
